import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PnlPage from '../../pages/pnl/PnlPage';
import PnlSummaryPage from '../../pages/pnl-summary/PnlSummaryPage';
import AbpPage from '../../pages/abp/AbpPage';
import CustomerAnalysisPage from '../../pages/customer-analysis/CustomerAnalysisPage';
import OuAnalysisPage from '../../pages/ou-analysis/OuAnalysisPage';
import UsersPage from '../../pages/users/UsersPage';
import RolesPage from '../../pages/roles/RolesPage';
import Layout from '../../components/layout';
import LoginPage from '../../pages/login/LoginPage';
import CustomerDataPage from '../../pages/customer-data/CustomerDataPage';
import BranchDataPageWrapper from "../../pages/customer-data/BranchDataPageWrapper"
import RunsPage from '../../pages/run-outstanding';
import PnlRunsPage from '../../pages/run-pnl';
import UnbilledRunsPage from '../../pages/run-unbilled';
import CnRegisterRunsPage from '../../pages/run-cn-register';
import LhcRegisterRunsPage from '../../pages/run-lhc-register';
import { useAuth } from 'contexts/AuthContext';
import { CustomerDataFiltersProvider } from 'contexts/CustomerDataFiltersContext';
import LhcAnalysisPage from '../../pages/lhc-analysis/LhcAnalysisPage';
import AraPage from 'pages/ara/AraPage';
import UarPage from 'pages/uar/UarPage';
import KeyAccountsLandingPage from 'pages/key-accounts/KeyAccountsLandingPage';
import KeyAccountDetailPage from 'pages/key-accounts/KeyAccountDetailPage';
import { updateKeyAccountsDropdowns } from 'pages/key-accounts/api';
import BranchNetworkPage from 'pages/branch-network/BranchNetworkPage';
import ShortPaymentsPage from 'pages/short-payments/ShortPaymentsPage';
import UnbilledPage from 'pages/unbilled/UnbilledPage';
import CustomerHealthPage from 'pages/customer-health/CustomerHealthPage';
import FuelPricesPage from 'pages/fuel/FuelPricesPage';
import RNSReportPage from 'pages/rns/RNSReportPage';
import CNHoldReportPage from 'pages/cn-hold/CNHoldReportPage';
import PendingPodReportPage from 'pages/pending-pod-report/PendingPodReportPage';
import ContractsPage from 'pages/contracts/ContractsPage';
import ContractDetailPage from 'pages/contracts/ContractDetailPage';
import VendorContractsPage from 'pages/vendor-contracts/VendorContractsPage';
import VendorContractFormPage from 'pages/vendor-contracts/VendorContractFormPage';
import VendorContractDetailPage from 'pages/vendor-contracts/VendorContractDetailPage';

function FullScreenSpinner() {
  return (
    <div className="w-screen h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, authLoading } = useAuth();
  if (authLoading) return <FullScreenSpinner />;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, authLoading } = useAuth();
  if (authLoading) return null;
  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
};

const DefaultRoute: React.FC = () => {
  const { menuPermissions, menuPermissionsLoading } = useAuth();

  if (menuPermissionsLoading) return <FullScreenSpinner />;

  const firstMenu = menuPermissions?.[0];
  const firstSubmenu = firstMenu?.submenu?.[0];

  const redirectPath = firstSubmenu?.route_path || "/reports/pnl";

  return <Navigate to={redirectPath} replace />;
};

const AppRouter: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/" element={<ProtectedRoute><DefaultRoute /></ProtectedRoute>} />
      <Route path="/dashboard/customer-analysis" element={<ProtectedRoute><Layout><CustomerAnalysisPage /></Layout></ProtectedRoute>} />
      <Route path="/dashboard/customer-health" element={<ProtectedRoute><Layout><CustomerHealthPage /></Layout></ProtectedRoute>} />
      <Route path="/dashboard/ou-analysis" element={<ProtectedRoute><Layout><OuAnalysisPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/pnl" element={<ProtectedRoute><Layout><PnlPage /></Layout></ProtectedRoute>} />
      <Route path='/reports/lhc-analysis' element={<ProtectedRoute><Layout><LhcAnalysisPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/performance-comparison" element={<ProtectedRoute><Layout><PnlSummaryPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/abp" element={<ProtectedRoute><Layout><AbpPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/ara" element={<ProtectedRoute><Layout><AraPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/short-payments" element={<ProtectedRoute><Layout><ShortPaymentsPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/uar" element={<ProtectedRoute><Layout><UarPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/pending-pod" element={<ProtectedRoute><Layout><PendingPodReportPage /></Layout></ProtectedRoute>} />
      <Route path="/settings/users" element={<ProtectedRoute><Layout><UsersPage /></Layout></ProtectedRoute>} />
      <Route path="/settings/roles" element={<ProtectedRoute><Layout><RolesPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/customer-data" element={<ProtectedRoute><Layout><CustomerDataFiltersProvider><CustomerDataPage /></CustomerDataFiltersProvider></Layout></ProtectedRoute>} />
      <Route path="/reports/customer-data/branches" element={<ProtectedRoute><Layout><CustomerDataFiltersProvider><BranchDataPageWrapper /></CustomerDataFiltersProvider></Layout></ProtectedRoute>} />
      <Route path="/settings/run/outstanding" element={<ProtectedRoute><Layout><RunsPage /></Layout></ProtectedRoute>} />
      <Route path="/settings/run/pnl" element={<ProtectedRoute><Layout><PnlRunsPage /></Layout></ProtectedRoute>} />
      <Route path="/settings/run/unbilled" element={<ProtectedRoute><Layout><UnbilledRunsPage /></Layout></ProtectedRoute>} />
      <Route path="/settings/run/cn-register" element={<ProtectedRoute><Layout><CnRegisterRunsPage /></Layout></ProtectedRoute>} />
      <Route path="/settings/run/lhc-register" element={<ProtectedRoute><Layout><LhcRegisterRunsPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/customer-data" element={<ProtectedRoute><Layout><CustomerDataFiltersProvider key="pnl"><CustomerDataPage /></CustomerDataFiltersProvider></Layout></ProtectedRoute>} />
      <Route path="/reports/customer-data/branches" element={<ProtectedRoute><Layout><CustomerDataFiltersProvider key="pnl"><BranchDataPageWrapper /></CustomerDataFiltersProvider></Layout></ProtectedRoute>} />
      <Route path="/reports/key-accounts" element={<ProtectedRoute><Layout><CustomerDataFiltersProvider key="key-accounts" dropdownsApi={updateKeyAccountsDropdowns}><KeyAccountsLandingPage /></CustomerDataFiltersProvider></Layout></ProtectedRoute>} />
      <Route path="/reports/key-accounts/:customerCode" element={<ProtectedRoute><Layout><CustomerDataFiltersProvider key="key-accounts" dropdownsApi={updateKeyAccountsDropdowns}><KeyAccountDetailPage /></CustomerDataFiltersProvider></Layout></ProtectedRoute>} />
      <Route path="/reports/branch-network" element={<ProtectedRoute><Layout><CustomerDataFiltersProvider key="branch-network"><BranchNetworkPage /></CustomerDataFiltersProvider></Layout></ProtectedRoute>} />
      <Route path="/reports/unbilled" element={<ProtectedRoute><Layout><UnbilledPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/diesel-prices" element={<ProtectedRoute><Layout><FuelPricesPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/rns" element={<ProtectedRoute><Layout><RNSReportPage /></Layout></ProtectedRoute>} />
      <Route path="/reports/cn-stock" element={<ProtectedRoute><Layout><CNHoldReportPage /></Layout></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><Layout><ContractsPage /></Layout></ProtectedRoute>} />
      <Route path="/contracts/:id" element={<ProtectedRoute><Layout><ContractDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/contracts/vendor" element={<ProtectedRoute><Layout><VendorContractsPage /></Layout></ProtectedRoute>} />
      <Route path="/contracts/vendor/new" element={<ProtectedRoute><Layout><VendorContractFormPage /></Layout></ProtectedRoute>} />
      <Route path="/contracts/vendor/:id" element={<ProtectedRoute><Layout><VendorContractDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/contracts/vendor/:id/edit" element={<ProtectedRoute><Layout><VendorContractFormPage /></Layout></ProtectedRoute>} />
    </Routes>
  );
};

export default AppRouter;
